import dayjs from 'dayjs'

import type { CalendarDateGroup, CalendarEvent } from './calendar'

export const parseTimeToMinutes = (value: string) => {
  const [hours = '0', minutes = '0'] = value.split(':')
  return Number(hours) * 60 + Number(minutes)
}

export const toDateValue = (value?: string | Date) => {
  if (!value) {
    return undefined
  }

  return value instanceof Date ? value : new Date(value)
}

export const toInternalDateValue = (value?: string | Date) => {
  if (!value) {
    return undefined
  }

  return value instanceof Date ? value : new Date(value)
}

export const normalizeDateLikeValue = (value: unknown) => {
  if (value instanceof Date) {
    return value.toISOString()
  }

  if (typeof value === 'string') {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? value : date.toISOString()
  }

  return value
}

export const toCalendarEvent = (event: CalendarEvent): CalendarEvent => ({
  ...event,
  start: toInternalDateValue(event.start) || event.start,
  end: toInternalDateValue(event.end) || event.end,
})

export const toCalendarEvents = (events: CalendarEvent[]) =>
  events.map(toCalendarEvent)

export const normalizeEventValue = (value: unknown): unknown => {
  const normalizedDateLikeValue = normalizeDateLikeValue(value)
  if (normalizedDateLikeValue !== value) {
    return normalizedDateLikeValue
  }

  if (Array.isArray(value)) {
    return value.map(normalizeEventValue)
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((result, key) => {
        result[key] = normalizeEventValue(
          (value as Record<string, unknown>)[key],
        )
        return result
      }, {})
  }

  return value
}

export const getEventsSignature = (events: CalendarEvent[]) =>
  JSON.stringify(events.map(normalizeEventValue))

export const isSameEvents = (left: CalendarEvent[], right: CalendarEvent[]) =>
  getEventsSignature(left) === getEventsSignature(right)

export const isEventOverlappingRange = (
  event: CalendarEvent,
  rangeStart: Date,
  rangeEnd: Date,
) => {
  const start = toDateValue(event.start)
  const end = toDateValue(event.end) || start
  if (
    !start ||
    Number.isNaN(start.getTime()) ||
    !end ||
    Number.isNaN(end.getTime())
  ) {
    return false
  }

  const normalizedRangeStart = dayjs(rangeStart).startOf('day').toDate()
  const normalizedRangeEnd = dayjs(rangeEnd).endOf('day').toDate()
  const effectiveEnd = new Date(Math.max(end.getTime() - 1, start.getTime()))

  return (
    start.getTime() <= normalizedRangeEnd.getTime() &&
    effectiveEnd.getTime() >= normalizedRangeStart.getTime()
  )
}

export const isCrossDayEvent = (event: CalendarEvent) => {
  const start = toDateValue(event.start)
  const end = toDateValue(event.end)

  if (event.allDay) {
    return true
  }

  if (
    !start ||
    Number.isNaN(start.getTime()) ||
    !end ||
    Number.isNaN(end.getTime())
  ) {
    return false
  }

  const crossesDayBoundary = dayjs(end)
    .startOf('day')
    .isAfter(dayjs(start).startOf('day'))
  if (!crossesDayBoundary) {
    return false
  }

  const dayMs = 24 * 60 * 60 * 1000
  const durationMs = end.getTime() - start.getTime()
  return durationMs >= dayMs
}

export const getCrossDaySortMetrics = (event: CalendarEvent) => {
  const start = toDateValue(event.start)
  const end = toDateValue(event.end) || start
  if (
    !start ||
    Number.isNaN(start.getTime()) ||
    !end ||
    Number.isNaN(end.getTime())
  ) {
    return {
      spanDays: 0,
      durationMinutes: 0,
      startAt: Number.MAX_SAFE_INTEGER,
    }
  }

  const effectiveEnd = new Date(Math.max(end.getTime() - 1, start.getTime()))
  const spanDays = Math.max(
    dayjs(effectiveEnd)
      .startOf('day')
      .diff(dayjs(start).startOf('day'), 'day') + 1,
    1,
  )
  const durationMinutes = Math.max(
    Math.round((end.getTime() - start.getTime()) / 60000),
    0,
  )

  return {
    spanDays,
    durationMinutes,
    startAt: start.getTime(),
  }
}

export const formatEventDateTime = (date: Date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${month}-${day} ${hours}:${minutes}`
}

export const formatEventTime = (date: Date) => {
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${hours}:${minutes}`
}

export const getEventStartTimestamp = (event: CalendarEvent) => {
  const start = toDateValue(event.start)
  return start && !Number.isNaN(start.getTime()) ? start.getTime() : 0
}

export const getEventDurationMinutes = (event: CalendarEvent) => {
  const start = toDateValue(event.start)
  const end = toDateValue(event.end)
  if (
    !start ||
    Number.isNaN(start.getTime()) ||
    !end ||
    Number.isNaN(end.getTime())
  ) {
    return 0
  }

  return Math.max(Math.round((end.getTime() - start.getTime()) / 60000), 0)
}

export const getEventSpanDays = (event: CalendarEvent) => {
  const start = toDateValue(event.start)
  const end = toDateValue(event.end)
  if (
    !start ||
    Number.isNaN(start.getTime()) ||
    !end ||
    Number.isNaN(end.getTime())
  ) {
    return 1
  }

  const startDay = dayjs(start).startOf('day')
  const endDay = dayjs(end).startOf('day')
  const diffDays = endDay.diff(startDay, 'day')

  return Math.max(diffDays + 1, 1)
}

export const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

export const toEditDate = (value: unknown) => {
  if (!value) {
    return undefined
  }

  if (dayjs.isDayjs(value)) {
    return value.toDate()
  }

  const date = toDateValue(value as string | Date)
  if (!date || Number.isNaN(date.getTime())) {
    return undefined
  }

  return date
}

export const createDraftClassToken = () =>
  `ll-calendar__event-draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

export const mergeClassName = (
  currentClass: CalendarEvent['class'],
  nextToken: string,
) => {
  const tokens = String(currentClass || '')
    .split(/\s+/)
    .filter(Boolean)
  if (!tokens.includes(nextToken)) {
    tokens.push(nextToken)
  }

  return tokens.join(' ')
}

export const stripClassName = (
  currentClass: CalendarEvent['class'],
  removeToken: string,
) =>
  String(currentClass || '')
    .split(/\s+/)
    .filter((token) => token && token !== removeToken)
    .join(' ')

export const hasClassToken = (currentClass: CalendarEvent['class'], token: string) =>
  String(currentClass || '')
    .split(/\s+/)
    .filter(Boolean)
    .includes(token)

export const isToday = (date: Date) => {
  const today = new Date()
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  )
}

export const hasActiveTextSelection = () => {
  if (typeof window === 'undefined' || !window.getSelection) {
    return false
  }

  const selection = window.getSelection()
  if (!selection) {
    return false
  }

  return !selection.isCollapsed && Boolean(selection.toString().trim())
}

// ---- 农历日期转换 ----

const LUNAR_DAY_NAMES = [
  '初一', '初二', '初三', '初四', '初五', '初六', '初七', '初八', '初九', '初十',
  '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十',
  '廿一', '廿二', '廿三', '廿四', '廿五', '廿六', '廿七', '廿八', '廿九', '三十',
]

let _lunarFormatter: Intl.DateTimeFormat | null | undefined

const getLunarFormatter = (): Intl.DateTimeFormat | null => {
  if (_lunarFormatter !== undefined) {
    return _lunarFormatter
  }
  try {
    _lunarFormatter = new Intl.DateTimeFormat('zh-CN-u-ca-chinese', {
      month: 'long',
      day: 'numeric',
    })
  } catch {
    _lunarFormatter = null
  }
  return _lunarFormatter
}

/**
 * 将公历日期转换为农历日期字符串。
 * - 农历初一返回月份名称（如"正月"、"腊月"）
 * - 其余日期返回日期名称（如"初二"、"十五"）
 */
export const getLunarDate = (date: Date | string): string => {
  const d = date instanceof Date ? date : new Date(date as string)
  if (!d || Number.isNaN(d.getTime())) return ''

  const formatter = getLunarFormatter()
  if (!formatter) return ''

  try {
    const parts = formatter.formatToParts(d)
    const monthPart = parts.find((p) => p.type === 'month')?.value ?? ''
    const dayPart = parts.find((p) => p.type === 'day')?.value ?? ''
    const dayNum = parseInt(dayPart, 10)

    if (dayNum === 1) {
      return monthPart
    }

    return !Number.isNaN(dayNum) && dayNum >= 1 && dayNum <= 30
      ? LUNAR_DAY_NAMES[dayNum - 1]
      : dayPart
  } catch {
    return ''
  }
}

// ---- 日期组（dateGroups）展开 / 折叠 ----

/**
 * 宽容地将任意「类日期」值解析为原生 Date：
 * 支持 Date / dayjs / 秒级或毫秒级时间戳 / ISO 字符串；无法解析返回 null。
 */
export const toDateLike = (value: unknown): Date | null => {
  if (value == null || value === '') {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (dayjs.isDayjs(value)) {
    const date = (value as dayjs.Dayjs).toDate()
    return Number.isNaN(date.getTime()) ? null : date
  }

  if (typeof value === 'number') {
    // 10 位按秒处理，13 位按毫秒处理
    const ms = value < 1e12 ? value * 1000 : value
    const date = new Date(ms)
    return Number.isNaN(date.getTime()) ? null : date
  }

  if (typeof value === 'string') {
    const numeric = Number(value)
    if (!Number.isNaN(numeric) && /^\d+$/.test(value.trim())) {
      return toDateLike(numeric)
    }
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }

  return null
}

/** Date → "YYYY-MM-DD HH:mm:ss" */
export const formatDateGroupDateTime = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

/**
 * 用 `{字段名}` 占位符模板生成标题，format 为空时回退到 record.title。
 */
export const applyDateGroupTitleFormat = (
  record: Record<string, any>,
  format?: string,
) => {
  if (!format) {
    return record.title != null ? String(record.title) : ''
  }

  return format.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = record[key]
    return value == null ? '' : String(value)
  })
}

/** 过滤出可用的日期组（启用 + 含 id + 含 startField）。 */
export const getEnabledDateGroups = (
  groups?: CalendarDateGroup[],
): CalendarDateGroup[] =>
  (groups ?? []).filter(
    (group) =>
      group && group.enabled !== false && Boolean(group.id) && Boolean(group.startField),
  )

const COMPOSITE_ID_SEPARATOR = '_'

/** 生成派生事件复合 ID：`${rawRecordId}_${groupId}`。 */
export const buildCompositeEventId = (rawRecordId: string, groupId: string) =>
  `${rawRecordId}${COMPOSITE_ID_SEPARATOR}${groupId}`

/**
 * 将一批原始记录按日期组展开为多条派生事件。
 *
 * @param records 原始数据列表
 * @param groups  已过滤的可用日期组（调用方负责 getEnabledDateGroups）
 * @param format  标题占位符模板
 */
export const buildDateGroupEvents = (
  records: CalendarEvent[],
  groups: CalendarDateGroup[],
  format?: string,
): CalendarEvent[] => {
  const result: CalendarEvent[] = []

  records.forEach((record, index) => {
    if (!record || typeof record !== 'object') {
      return
    }

    const rawRecordId = String(record.id ?? index)

    // 该记录是否产生了任一日期组派生事件
    let produced = false

    groups.forEach((group) => {
      const startDate = toDateLike(record[group.startField])
      if (!startDate) {
        return
      }
      produced = true

      const endVal = group.endField ? record[group.endField] : null
      const endDate = endVal ? toDateLike(endVal) || startDate : startDate

      const event: CalendarEvent = {
        ...record,
        id: buildCompositeEventId(rawRecordId, group.id),
        title: applyDateGroupTitleFormat(record, format),
        start: formatDateGroupDateTime(startDate),
        end: formatDateGroupDateTime(endDate),
        rawRecordId,
        _dateGroupId: group.id,
      }

      // 颜色优先级：backgroundColor/borderColor > eventColor > labelColor
      const resolvedEventColor = group.eventColor ?? group.labelColor
      if (resolvedEventColor != null) {
        event.eventColor = resolvedEventColor
      }
      if (group.backgroundColor != null) {
        event.backgroundColor = group.backgroundColor
      }
      if (group.borderColor != null) {
        event.borderColor = group.borderColor
      }
      if (group.color != null) {
        event.color = group.color
      }

      // 标签优先级：tag/tagText > labelText
      const resolvedTag = group.tag ?? group.tagText ?? group.labelText
      if (resolvedTag != null) {
        event.tag = resolvedTag
      }
      if (group.tagColor != null) {
        event.tagColor = group.tagColor
      }

      result.push(event)
    })

    // 未匹配任何日期组、但本身是默认日程结构（含原生 start）的记录：原样透传。
    // 典型场景：用户在日历内新建的事件只有原生 start/end、尚无日期组字段；
    // 若此处丢弃会与 foldDateGroupEvents 的 passthrough 不对称，导致 expand/fold
    // 永不收敛、internalEvents 与 modelValue 反复回写而触发递归更新。
    if (!produced && toDateLike(record.start)) {
      result.push(record)
    }
  })

  return result
}

/**
 * 解析复合事件 ID，返回 { rawRecordId, groupId }；
 * 无法识别（不含分隔符）时 groupId 为 null。
 */
export const parseCompositeEventId = (
  id: string | number | undefined,
  groups: CalendarDateGroup[],
): { rawRecordId: string; groupId: string | null } => {
  const raw = String(id ?? '')
  // 精确匹配已知 groupId 后缀，避免 rawRecordId 含下划线时误切分
  for (const group of groups) {
    const suffix = `${COMPOSITE_ID_SEPARATOR}${group.id}`
    if (raw.endsWith(suffix) && raw.length > suffix.length) {
      return { rawRecordId: raw.slice(0, -suffix.length), groupId: group.id }
    }
  }
  return { rawRecordId: raw, groupId: null }
}

/**
 * 将（被基础组件编辑/拖拽过的）派生事件折叠回原始记录列表。
 *
 * - 派生事件依据复合 ID 解析出 rawRecordId/groupId，把 start/end 写回对应字段；
 * - 派生事件携带的其它字段（除内部标记/渲染字段）一并合并回原始记录；
 * - 派生事件的「消失」视为对应原始记录被删除（其所有派生事件均不在列表中时移除记录）；
 * - 非派生事件（用户在日历内新建、或本就是原生 start/end 记录）原样保留。
 *
 * @param events    当前展示层事件列表（可能被编辑过）
 * @param baseRecords 上一次的原始记录列表，用于保留未展示字段与判定删除
 * @param groups    已过滤的可用日期组
 */
export const foldDateGroupEvents = (
  events: CalendarEvent[],
  baseRecords: CalendarEvent[],
  groups: CalendarDateGroup[],
): CalendarEvent[] => {
  // 渲染/内部字段不回写原始记录
  const INTERNAL_KEYS = new Set([
    'id',
    'start',
    'end',
    'title',
    'rawRecordId',
    '_dateGroupId',
    '_eid',
    'class',
    'eventColor',
    'backgroundColor',
    'borderColor',
    'color',
    'tag',
    'tagColor',
  ])

  const recordMap = new Map<string, CalendarEvent>()
  const order: string[] = []
  baseRecords.forEach((record, index) => {
    if (!record || typeof record !== 'object') {
      return
    }
    const key = String(record.id ?? index)
    recordMap.set(key, { ...record })
    order.push(key)
  })

  // 记录本轮哪些 rawRecordId 仍有派生事件存活，以及具体存活的 (rawId, groupId)
  const aliveRawIds = new Set<string>()
  const aliveGroupKeys = new Set<string>()
  const passthrough: CalendarEvent[] = []

  events.forEach((event) => {
    if (!event || typeof event !== 'object') {
      return
    }

    const { rawRecordId, groupId } = parseCompositeEventId(event.id, groups)
    const group = groupId ? groups.find((g) => g.id === groupId) : undefined

    if (!group) {
      // 非派生事件：原样透传（用户在日历内新建的事件等）
      passthrough.push(event)
      return
    }

    aliveRawIds.add(rawRecordId)
    aliveGroupKeys.add(`${rawRecordId}::${group.id}`)

    const target: CalendarEvent =
      recordMap.get(rawRecordId) ?? ({ id: rawRecordId } as CalendarEvent)
    if (!recordMap.has(rawRecordId)) {
      recordMap.set(rawRecordId, target)
      order.push(rawRecordId)
    }

    // 将 start/end 写回日期组字段
    const startDate = toDateLike(event.start)
    if (startDate) {
      target[group.startField] = formatDateGroupDateTime(startDate)
    }
    if (group.endField) {
      const endDate = toDateLike(event.end)
      if (endDate) {
        target[group.endField] = formatDateGroupDateTime(endDate)
      }
    }

    // 合并派生事件携带的其它业务字段
    Object.keys(event).forEach((key) => {
      if (!INTERNAL_KEYS.has(key)) {
        target[key] = event[key]
      }
    })
  })

  // 删除判定：
  // - 原始记录曾产生过派生事件、但本轮已无任一派生事件存活 → 视为整条记录被删除；
  // - 原始记录仍存活、但其中某个日期组的派生事件被单独删除 → 清空该组对应日期字段，
  //   避免下次展开时该派生事件「复活」。
  const result: CalendarEvent[] = []
  order.forEach((key) => {
    const record = recordMap.get(key)
    if (!record) {
      return
    }
    const producedDerived = groups.some((group) => toDateLike(record[group.startField]))
    if (producedDerived && !aliveRawIds.has(key)) {
      // 该原始记录的所有派生事件都被删除 → 丢弃整条记录
      return
    }
    if (aliveRawIds.has(key)) {
      groups.forEach((group) => {
        if (!aliveGroupKeys.has(`${key}::${group.id}`)) {
          // 该组派生事件已被删除 → 清空对应日期字段
          delete record[group.startField]
          if (group.endField) {
            delete record[group.endField]
          }
        }
      })
    }
    result.push(record)
  })

  return [...result, ...passthrough]
}
